import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Chip, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  RefreshRounded as RefreshIcon,
  ArrowForwardRounded as ArrowIcon,
  EventBusyOutlined as ExpiryIcon,
  CheckCircleOutlineRounded as ClearIcon,
  PaymentsOutlined as PayrollIcon,
  VerifiedUserOutlined as ComplianceIcon,
  PersonSearchOutlined as HiringIcon,
  LocalShippingOutlined as FleetIcon,
  AccountTreeOutlined as OpsIcon,
} from '@mui/icons-material';
import DocumentService, { AttentionItem, ComplianceBoard } from '../../api/services/document.service';
import WorkflowService, { WorkflowInstance } from '../../api/services/workflow.service';
import PayrollService, { PayrollRun, Person as PayPerson, Reports } from '../../api/services/payroll.service';
import HiringService, { Candidate, Insights } from '../../api/services/hiring.service';
import VehicleService, { Vehicle } from '../../api/services/vehicle.service';
import apiClient from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import PageHeader from '../../components/ui/PageHeader';
import PageBody from '../../components/ui/PageBody';
import { EmptyState, LoadingState } from '../../components/ui/States';
import { ObligationPill, describeExpiry } from '../../components/files/vaultUI';
import { isEscalated, waitingOn } from '../workflows/workflowUI';
import { STAGE_LABEL, STAGE_COLOR } from '../hiring/hiringUI';
import { money, percent, shortDate } from '../payroll/payrollUI';
import { status as statusTokens } from '../../theme';
import DashboardPanel from './DashboardPanel';

// The overview.
//
// Four things run this company — pay, paperwork, hiring, trucks — and
// each gets one card up top with its number, where it is heading, and a
// small picture of the last few weeks. Under them, one list: everything
// that needs somebody today, from every corner, worst first. Then the
// two pictures worth a second look (payroll ratio by week, the hiring
// pipeline), what is running, and who did what lately.
//
// Nothing here is a new API. The page reads what the other screens
// read, and colour means the same thing it means there: a problem.
// A healthy morning is a calm page.

type Tone = 'neutral' | 'ok' | 'warn' | 'danger';
const toneColor: Record<Tone, string | undefined> = { neutral: undefined, ok: statusTokens.ok, warn: statusTokens.warn, danger: statusTokens.danger };

const ago = (iso: string): string => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? 'yesterday' : `${d}d ago`;
};

const humanise = (action: string): string => {
  const [entity, verb] = action.split('.');
  const words = `${(entity || '').replace(/_/g, ' ')} ${(verb || '').replace(/_/g, ' ')}`.trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

interface AuditEntry { _id: string; action: string; entityLabel?: string; actorName?: string; at: string }

// A sign-in's entity is the person's own email, already named by the
// actor — say what happened, not what the log row holds.
const describeEntry = (e: AuditEntry): string => {
  if (e.action === 'auth.login') return 'Signed in';
  return `${humanise(e.action)}${e.entityLabel ? ` · ${e.entityLabel}` : ''}`;
};

/** Back-to-back repeats (five views of one statement) read as one line with a count. */
const collapseRuns = (entries: AuditEntry[]): { entry: AuditEntry; count: number }[] => {
  const out: { entry: AuditEntry; count: number }[] = [];
  for (const e of entries) {
    const prev = out[out.length - 1];
    if (prev && prev.entry.actorName === e.actorName && describeEntry(prev.entry) === describeEntry(e)) prev.count += 1;
    else out.push({ entry: e, count: 1 });
  }
  return out;
};

// ---------------------------------------------------------------- hero

/** Eight bars, no axes: the shape of the last two months at a glance. */
const Spark: React.FC<{ values: (number | null)[]; tone?: Tone; height?: number }> = ({ values, tone = 'neutral', height = 36 }) => {
  const max = Math.max(1, ...values.map((v) => v || 0));
  return (
    <Stack direction="row" alignItems="flex-end" gap={0.4} sx={{ height }}>
      {values.map((v, i) => (
        <Box key={i} sx={{ flex: 1, height: `${((v || 0) / max) * 100}%`, minHeight: v ? 2 : 0, bgcolor: i === values.length - 1 ? (toneColor[tone] || 'primary.dark') : 'primary.light', borderRadius: '2px 2px 0 0', opacity: i === values.length - 1 ? 1 : 0.7 }} />
      ))}
    </Stack>
  );
};

/** A ring that fills to a percent. Untracked is an empty ring, never full. */
const Ring: React.FC<{ percent: number | null; tone: Tone; size?: number }> = ({ percent: p, tone, size = 56 }) => {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const fill = p === null ? 0 : Math.max(0, Math.min(100, p));
  return (
    <Box component="svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" sx={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={6} style={{ color: 'var(--mui-palette-grey-200, #eee)' }} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={toneColor[tone] || 'currentColor'} strokeWidth={6} strokeLinecap="round" strokeDasharray={`${(fill / 100) * c} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dasharray 400ms ease' }} />
    </Box>
  );
};

const Hero: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  line: React.ReactNode;
  tone?: Tone;
  visual?: React.ReactNode;
  chip?: { label: string; tone: Tone };
  onClick: () => void;
  loading?: boolean;
}> = ({ icon, label, value, line, tone = 'neutral', visual, chip, onClick, loading }) => (
  <Card
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    sx={{
      p: 2.25, minWidth: 0, cursor: 'pointer', position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      transition: 'box-shadow 150ms, transform 150ms',
      '&:hover': { boxShadow: '0 8px 24px rgba(22,21,15,0.08)', transform: 'translateY(-1px)' },
      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
      // A pseudo-element, not an inset shadow: the hover shadow replaced
      // the stripe, so the warning vanished exactly when you pointed at it.
      ...(tone !== 'neutral' && tone !== 'ok' && {
        '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: toneColor[tone] },
      }),
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ color: 'text.secondary' }}>
        {icon}
        <Typography variant="overline" sx={{ lineHeight: 1 }}>{label}</Typography>
      </Stack>
      {chip && <Chip size="small" label={chip.label} sx={{ height: 20, fontSize: 11, fontWeight: 700, color: toneColor[chip.tone] || 'text.secondary', bgcolor: 'transparent', border: '1px solid', borderColor: toneColor[chip.tone] || 'divider' }} />}
    </Stack>
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5}>
      <Typography noWrap sx={{ minWidth: 0, fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color: loading ? 'text.disabled' : 'text.primary' }}>
        {loading ? '—' : value}
      </Typography>
      {visual && <Box sx={{ width: 88, flexShrink: 0 }}>{visual}</Box>}
    </Stack>
    {/* Full width and up to two lines: the supporting fact is the reason
        the number matters, and it used to be clipped at "58.1…". */}
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.75, minHeight: '2.66em', lineHeight: 1.33 }}
    >
      {line}
    </Typography>
  </Card>
);

// ------------------------------------------------------------- needs you

interface Todo {
  key: string;
  domain: 'payroll' | 'files' | 'hiring' | 'fleet' | 'ops';
  severity: 0 | 1 | 2; // 2 = now, 1 = soon, 0 = fyi
  title: string;
  detail: string;
  to: string;
  pill?: React.ReactNode;
}

const DOMAIN: Record<Todo['domain'], { icon: React.ReactNode; label: string }> = {
  payroll: { icon: <PayrollIcon sx={{ fontSize: 18 }} />, label: 'Payroll' },
  files: { icon: <ComplianceIcon sx={{ fontSize: 18 }} />, label: 'Files' },
  hiring: { icon: <HiringIcon sx={{ fontSize: 18 }} />, label: 'Hiring' },
  fleet: { icon: <FleetIcon sx={{ fontSize: 18 }} />, label: 'Fleet' },
  ops: { icon: <OpsIcon sx={{ fontSize: 18 }} />, label: 'Operations' },
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, canArea } = useAuthStore();
  // What the dashboard shows follows the access matrix, like the menus.
  const canSeeVault = canArea('files', 'edit');
  const canSeePay = canArea('payroll');
  const canSeeReports = canArea('reports');
  const canSeeAudit = canArea('audit');

  const [board, setBoard] = useState<ComplianceBoard | null>(null);
  const [attention, setAttention] = useState<AttentionItem[]>([]);
  const [running, setRunning] = useState<WorkflowInstance[] | null>(null);
  const [runs, setRuns] = useState<PayrollRun[] | null>(null);
  const [reports, setReports] = useState<Reports | null>(null);
  const [payPeople, setPayPeople] = useState<PayPerson[] | null>(null);
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [insights, setInsights] = useState<Insights | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[] | null>(null);
  const [activity, setActivity] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const skip = () => Promise.reject(new Error('skip'));
    try {
      // Settled rather than all: whoever can see a corner gets that corner.
      const results = await Promise.allSettled([
        canSeeVault ? DocumentService.getBoard() : skip(),
        canSeeVault ? DocumentService.getAttention(8) : skip(),
        WorkflowService.getInstances('active'),
        canSeePay ? PayrollService.runs() : skip(),
        canSeeReports ? PayrollService.reports(8) : skip(),
        canSeePay ? PayrollService.people() : skip(),
        canSeeVault ? HiringService.list({ outcome: 'open', pageSize: 200 }) : skip(),
        canSeeVault ? HiringService.insights() : skip(),
        canSeeVault ? VehicleService.list() : skip(),
        // More than we show: repeats collapse, and eight lines should survive.
        canSeeAudit ? (apiClient.get('/api/audit?pageSize=30') as Promise<any>) : skip(),
      ]);
      const got = <T,>(i: number): T | null => (results[i].status === 'fulfilled' ? ((results[i] as PromiseFulfilledResult<T>).value ?? null) : null);
      const b = got<ComplianceBoard>(0); if (b) setBoard(b);
      const a = got<AttentionItem[]>(1); if (a) setAttention(a);
      const w = got<WorkflowInstance[]>(2); if (w) setRunning(w);
      const r = got<PayrollRun[]>(3); if (r) setRuns(r);
      const rep = got<Reports>(4); if (rep) setReports(rep);
      const pp = got<PayPerson[]>(5); if (pp) setPayPeople(pp);
      const c = got<{ items: Candidate[] }>(6); if (c) setCandidates(c.items || []);
      const ins = got<Insights>(7); if (ins) setInsights(ins);
      const v = got<Vehicle[]>(8); if (v) setVehicles(v);
      const au = got<{ items: AuditEntry[] }>(9); if (au) setActivity(au.items || []);
      if (results.every((x) => x.status === 'rejected')) setError('Could not load your overview.');
    } finally {
      setLoading(false);
    }
  }, [canSeeVault, canSeePay, canSeeReports, canSeeAudit]);

  useEffect(() => { load(); }, [load]);

  const summary = board?.summary;
  const escalated = useMemo(() => (running ?? []).filter(isEscalated), [running]);
  // Escalated runs are already at the top of Needs you; listing them again
  // under Operations was the same row twice on one screen.
  const calmRuns = useMemo(() => (running ?? []).filter((w) => !isEscalated(w)), [running]);
  const lately = useMemo(() => collapseRuns(activity).slice(0, 8), [activity]);

  // ---- payroll facts
  const latest = runs?.[0] || null;
  const previous = runs?.[1] || null;
  const ratioDelta = latest?.ratio != null && previous?.ratio != null ? latest.ratio - previous.ratio : null;
  const noRules = (payPeople || []).filter((p) => !p.hasRules);
  const grossSeries = (reports?.weeks || []).map((w) => w.gross);
  const payTone: Tone = latest ? (latest.status === 'draft' ? 'warn' : ratioDelta !== null && ratioDelta > 3 ? 'warn' : 'neutral') : 'neutral';

  // ---- hiring facts
  const open = candidates || [];
  const lateOnUs = open.filter((c) => c.nextAction?.owedBy === 'us' && (c.nextAction?.overdueDays || 0) > 0);
  const quiet = open.filter((c) => (c.daysSinceActivity ?? 0) >= 7
    && (c.nextAction?.owedBy === 'them' || !!c.nextAction?.waitingOn?.some((w) => w.owedBy === 'them')));
  const hiredRecently = insights?.outcomes?.hired || 0;
  const pipeline = insights?.pipeline || [];
  const pipelineTotal = pipeline.reduce((t, p) => t + p.count, 0);

  // ---- fleet facts
  const fleet = useMemo(() => vehicles || [], [vehicles]);
  const onRoad = fleet.filter((v) => v.status === 'active').length;
  const inShop = fleet.filter((v) => v.status === 'maintenance').length;

  // ---- compliance
  const score = summary?.score || null;
  const complianceTone: Tone = !score ? 'neutral' : score.percent === 100 ? 'ok' : score.percent >= 80 ? 'warn' : 'danger';

  // ---- the one list
  const todos = useMemo<Todo[]>(() => {
    const out: Todo[] = [];
    if (latest && latest.status === 'draft') {
      out.push({ key: 'pay-draft', domain: 'payroll', severity: 2, title: `Finalize the week ending ${shortDate(latest.weekEnd)}`, detail: `${money(latest.totals.gross)} for ${latest.totals.paid} people, still a draft`, to: `/payroll/weeks/${latest._id}` });
    }
    if (noRules.length) {
      out.push({ key: 'pay-rules', domain: 'payroll', severity: latest?.status === 'draft' ? 2 : 1, title: `${noRules.length} ${noRules.length === 1 ? 'person has' : 'people have'} no pay rules`, detail: noRules.slice(0, 3).map((p) => p.name).join(', ') + (noRules.length > 3 ? '…' : ''), to: `/payroll/people/${noRules[0].accountId}` });
    }
    // One owner short several documents of the same status is one job, not
    // five rows that differ only in a policy name. Groups keep the server's
    // worst-first order (a group sits where its first item did).
    const docGroups: AttentionItem[][] = [];
    for (const item of attention) {
      const same = docGroups.find((g) => g[0].ownerType === item.ownerType && g[0].ownerId === item.ownerId && g[0].status === item.status);
      if (same) same.push(item); else docGroups.push([item]);
    }
    for (const group of docGroups.slice(0, 5)) {
      const item = group[0];
      const owner = item.ownerLabel || 'the company';
      out.push({
        key: `doc-${item._id}`,
        domain: 'files',
        severity: item.status === 'overdue' || item.status === 'lapsed' || item.status === 'missing' ? 2 : 1,
        title: group.length > 1 ? `${group.length} documents for ${owner}` : `${item.label} for ${owner}`,
        // The pill already says "Missing"; the line says which ones, or when.
        detail: group.length > 1
          ? group.map((g) => g.label).join(', ')
          : (item.dueAt && describeExpiry(item.dueAt)) || (item.status === 'missing' ? 'Nothing on file' : 'Needs a document'),
        to: `/files/entity/${item.ownerType}/${item.ownerId ?? 'company'}`,
        pill: <ObligationPill status={item.status} />,
      });
    }
    for (const c of lateOnUs.slice(0, 4)) {
      out.push({ key: `cand-${c._id}`, domain: 'hiring', severity: 2, title: `${c.firstName} ${c.lastName || ''}`.trim(), detail: `${c.nextAction?.label || 'Next step'} is ${c.nextAction?.overdueDays} ${c.nextAction?.overdueDays === 1 ? 'day' : 'days'} late, on us`, to: `/hiring/${c._id}` });
    }
    for (const w of escalated.slice(0, 3)) {
      out.push({ key: `wf-${w._id}`, domain: 'ops', severity: 2, title: w.label, detail: `Escalated, waiting on ${waitingOn(w)}`, to: `/operations/${w._id}` });
    }
    if (inShop) {
      out.push({ key: 'fleet-shop', domain: 'fleet', severity: 1, title: `${inShop} ${inShop === 1 ? 'vehicle' : 'vehicles'} in maintenance`, detail: fleet.filter((v) => v.status === 'maintenance').slice(0, 3).map((v) => v.unitNumber || v.vin || 'Unnamed').join(', '), to: '/fleet' });
    }
    if (quiet.length) {
      out.push({ key: 'cand-quiet', domain: 'hiring', severity: 0, title: `${quiet.length} ${quiet.length === 1 ? 'candidate has' : 'candidates have'} gone quiet`, detail: 'A week or more with the ball in their court', to: '/hiring/follow-up' });
    }
    return out.sort((a, b) => b.severity - a.severity);
  }, [latest, noRules, attention, lateOnUs, escalated, inShop, fleet, quiet]);

  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <>
      <DashboardPanel />

      <PageBody>
        <PageHeader
          title={user?.firstName ? `${greeting}, ${user.firstName}` : 'Overview'}
          subtitle={today}
          actions={
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
          }
        />

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* ------------------------------------------------------- heroes */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2.5 }}>
          {canSeePay && (
            <Hero
              icon={<PayrollIcon sx={{ fontSize: 18 }} />}
              label="Payroll"
              value={latest ? money(latest.totals.gross) : 'No weeks'}
              line={latest ? `Week ending ${shortDate(latest.weekEnd)} · ${percent(latest.ratio)} of revenue${ratioDelta !== null ? ` (${ratioDelta <= 0 ? '▾' : '▴'} ${Math.abs(ratioDelta).toFixed(1)} pts)` : ''}` : 'Upload a statement to start'}
              tone={payTone}
              chip={latest ? { label: latest.status === 'draft' ? 'Draft' : 'Finalized', tone: latest.status === 'draft' ? 'warn' : 'ok' } : undefined}
              visual={grossSeries.length > 1 ? <Spark values={grossSeries} tone={payTone} /> : undefined}
              onClick={() => navigate('/payroll')}
              loading={loading && !runs}
            />
          )}
          {canSeeVault && (
            <Hero
              icon={<ComplianceIcon sx={{ fontSize: 18 }} />}
              label="Compliance"
              // A null score is "not tracked", never 100%: the vault's rule.
              value={score ? `${score.percent}%` : 'Not tracked'}
              line={score ? `${score.satisfied} of ${score.total} obligations · ${summary?.needsAttention || 0} need attention` : 'Nothing reconciled yet'}
              tone={summary?.needsAttention ? 'danger' : complianceTone}
              visual={<Stack alignItems="flex-end"><Ring percent={score ? score.percent : null} tone={complianceTone} /></Stack>}
              onClick={() => navigate('/files/compliance')}
              loading={loading && !board}
            />
          )}
          {canSeeVault && (
            <Hero
              icon={<HiringIcon sx={{ fontSize: 18 }} />}
              label="Hiring"
              value={candidates ? `${open.length} open` : '—'}
              line={lateOnUs.length ? `${lateOnUs.length} late on our side · ${hiredRecently} hired lately` : `${hiredRecently} hired lately · ${quiet.length} gone quiet`}
              tone={lateOnUs.length ? 'danger' : 'neutral'}
              visual={pipelineTotal > 0 ? (
                <Stack direction="row" sx={{ height: 10, borderRadius: 5, overflow: 'hidden' }}>
                  {pipeline.map((p) => p.count > 0 && (
                    <Tooltip key={p.stage} title={`${STAGE_LABEL[p.stage]}: ${p.count}`}><Box sx={{ flex: p.count, bgcolor: STAGE_COLOR[p.stage] || 'primary.main' }} /></Tooltip>
                  ))}
                </Stack>
              ) : undefined}
              onClick={() => navigate('/hiring')}
              loading={loading && !candidates}
            />
          )}
          {canSeeVault && (
            <Hero
              icon={<FleetIcon sx={{ fontSize: 18 }} />}
              label="Fleet"
              // "0 on the road · 0 in maintenance · 0 retired" is an empty
              // fleet dressed as a metric; say it is empty and what fills it.
              value={!vehicles ? '—' : fleet.length === 0 ? 'No vehicles' : `${onRoad} on the road`}
              line={!vehicles ? '' : fleet.length === 0 ? 'Add your trucks to track them here' : `${inShop} in maintenance · ${fleet.length - onRoad - inShop} retired`}
              tone={inShop ? 'warn' : 'neutral'}
              visual={fleet.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={0.4} justifyContent="flex-end" sx={{ mt: 1 }}>
                  {fleet.filter((v) => v.status !== 'retired').slice(0, 24).map((v) => (
                    <Tooltip key={v._id} title={`${v.unitNumber || v.vin || 'Vehicle'}: ${v.status}`}><Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: v.status === 'active' ? 'primary.main' : statusTokens.warn }} /></Tooltip>
                  ))}
                </Stack>
              ) : undefined}
              onClick={() => navigate('/fleet')}
              loading={loading && !vehicles}
            />
          )}
        </Box>

        {loading && !board && !running && !runs ? (
          <LoadingState label="Loading your overview…" />
        ) : (
          // Two independent columns, not one grid: in a shared grid the tall
          // Needs-you list stretched the right column's rows and left a
          // dead gap under the payroll chart.
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 2, alignItems: 'start' }}>
            <Stack gap={2} sx={{ minWidth: 0 }}>
              {/* --------------------------------------------- needs you */}
              <Card>
                <SectionHead title="Needs you" moreLabel={todos.length ? `${todos.length} ${todos.length === 1 ? 'item' : 'items'}` : ''} />
                {todos.length === 0 ? (
                  <EmptyState
                    compact
                    icon={<ClearIcon />}
                    title="All clear"
                    description={board?.nextExpiry?.dueAt ? `Next up: ${board.nextExpiry.label} for ${board.nextExpiry.ownerLabel}, ${describeExpiry(board.nextExpiry.dueAt)}.` : 'Nothing is waiting on you right now.'}
                  />
                ) : (
                  <Box>
                    {todos.map((t) => (
                      <Row
                        key={t.key}
                        onOpen={() => navigate(t.to)}
                        accent={t.severity === 2 ? statusTokens.danger : t.severity === 1 ? statusTokens.warn : undefined}
                      >
                        <Tooltip title={DOMAIN[t.domain].label}><Box sx={{ color: 'text.secondary', display: 'flex' }}>{DOMAIN[t.domain].icon}</Box></Tooltip>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{t.title}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{t.detail}</Typography>
                        </Box>
                        {t.pill}
                        <ArrowIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </Row>
                    ))}
                  </Box>
                )}
              </Card>

              {/* --------------------------------------------- workflows */}
              <Card>
                <SectionHead title="Operations" moreLabel="Open operations" onMore={() => navigate('/operations')} />
                {calmRuns.length === 0 ? (
                  <EmptyState
                    compact
                    icon={<ExpiryIcon />}
                    title={escalated.length ? 'Nothing else running' : 'Nothing running'}
                    description={escalated.length
                      ? `${escalated.length} escalated ${escalated.length === 1 ? 'run is' : 'runs are'} at the top of Needs you.`
                      : 'Workflows started by a schedule or by hand will appear here while they are in progress.'}
                  />
                ) : (
                  <Box>
                    {calmRuns.slice(0, 6).map((instance) => (
                      <Row key={instance._id} onOpen={() => navigate(`/operations/${instance._id}`)}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{instance.label}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            Waiting on {waitingOn(instance)}
                          </Typography>
                        </Box>
                        <ArrowIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </Row>
                    ))}
                  </Box>
                )}
              </Card>
            </Stack>

            <Stack gap={2} sx={{ minWidth: 0 }}>
              {/* --------------------------------------- payroll by week */}
              {canSeePay && reports && reports.weeks.length > 0 && (
                <Card sx={{ p: 2 }}>
                  <SectionHead dense title="Payroll ratio by week" moreLabel="Reports" onMore={() => navigate('/reports/payroll')} />
                  <Stack direction="row" gap={0.75} sx={{ mt: 1.5 }}>
                    {reports.weeks.map((w, i) => {
                      const max = Math.max(1, ...reports.weeks.map((x) => x.ratio || 0));
                      const last = i === reports.weeks.length - 1;
                      return (
                        <Tooltip key={w.weekEnd} title={`${shortDate(w.weekEnd)}: ${percent(w.ratio)} of ${money(w.revenue)}${w.status === 'draft' ? ' (draft)' : ''}`} placement="top">
                          <Stack
                            alignItems="center"
                            gap={0.5}
                            role="link"
                            tabIndex={0}
                            onClick={() => navigate(`/payroll/weeks/${w.runId}`)}
                            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/payroll/weeks/${w.runId}`); }}
                            sx={{ flex: 1, minWidth: 0, cursor: 'pointer', borderRadius: 1, '&:hover .bar': { opacity: 0.85 }, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: last ? 'primary.dark' : 'text.secondary' }}>{w.ratio === null ? '—' : `${Math.round(w.ratio)}%`}</Typography>
                            {/* A fixed track, so the date under it can never be pushed out of the card. */}
                            <Box sx={{ width: '100%', height: 72, display: 'flex', alignItems: 'flex-end' }}>
                              <Box className="bar" sx={{ width: '100%', height: `${((w.ratio || 0) / max) * 100}%`, bgcolor: last ? 'primary.dark' : 'primary.light', borderRadius: '4px 4px 0 0', opacity: w.status === 'draft' ? 0.55 : 1 }} />
                            </Box>
                            <Typography variant="caption" color={last ? 'text.primary' : 'text.secondary'} fontWeight={last ? 600 : 400} noWrap>{shortDate(w.weekEnd)}</Typography>
                          </Stack>
                        </Tooltip>
                      );
                    })}
                  </Stack>
                </Card>
              )}

              {/* --------------------------------------- hiring pipeline */}
              {canSeeVault && insights && pipelineTotal > 0 && (
                <Card sx={{ p: 2 }}>
                  <SectionHead dense title="Hiring pipeline" moreLabel="Hiring" onMore={() => navigate('/hiring/list')} />
                  <Stack gap={0.25} sx={{ mt: 1 }}>
                    {pipeline.map((p) => (
                      <Stack
                        key={p.stage}
                        direction="row"
                        alignItems="center"
                        gap={1.25}
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate(`/hiring/list?stage=${p.stage}`)}
                        onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/hiring/list?stage=${p.stage}`); }}
                        sx={{ cursor: 'pointer', py: 0.5, mx: -1, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'grey.50' }, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}
                      >
                        <Typography variant="caption" sx={{ width: 96, color: 'text.secondary', flexShrink: 0 }}>{STAGE_LABEL[p.stage]}</Typography>
                        <Box sx={{ flex: 1, height: 8, bgcolor: 'grey.100', borderRadius: 4, overflow: 'hidden' }}>
                          <Box sx={{ width: `${(p.count / Math.max(1, ...pipeline.map((x) => x.count))) * 100}%`, height: '100%', bgcolor: STAGE_COLOR[p.stage] || 'primary.main', borderRadius: 4 }} />
                        </Box>
                        <Typography variant="body2" sx={{ width: 24, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: p.count ? 'text.primary' : 'text.disabled' }}>{p.count}</Typography>
                        <Typography variant="caption" sx={{ width: 52, color: p.oldestIdleDays && p.oldestIdleDays > 7 ? 'warning.dark' : 'text.disabled', flexShrink: 0 }}>{p.oldestIdleDays ? `${p.oldestIdleDays}d idle` : ''}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Card>
              )}

              {/* ----------------------------------------- recent activity */}
              {canSeeAudit && (
                <Card>
                  <SectionHead title="Lately" moreLabel="Audit trail" onMore={() => navigate('/settings/audit')} />
                  {lately.length === 0 ? (
                    <EmptyState compact title="Nothing yet" description="Who did what shows up here." />
                  ) : (
                    <Box>
                      {lately.map(({ entry: e, count }) => (
                        <Stack key={e._id} direction="row" alignItems="baseline" gap={1.5} sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                            <Box component="span" sx={{ fontWeight: 600 }}>{e.actorName || 'Someone'}</Box>
                            <Box component="span" sx={{ color: 'text.secondary' }}> · {describeEntry(e)}</Box>
                          </Typography>
                          {count > 1 && <Typography variant="caption" sx={{ flexShrink: 0, color: 'text.secondary', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>×{count}</Typography>}
                          <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, width: 56, textAlign: 'right' }}>{ago(e.at)}</Typography>
                        </Stack>
                      ))}
                    </Box>
                  )}
                </Card>
              )}
            </Stack>
          </Box>
        )}
      </PageBody>
    </>
  );
};

/** A list row you can click or reach with the keyboard; `accent` is the severity edge. */
const Row: React.FC<{ onOpen: () => void; accent?: string; children: React.ReactNode }> = ({ onOpen, accent, children }) => (
  <Box
    role="link"
    tabIndex={0}
    onClick={onOpen}
    onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, cursor: 'pointer',
      borderTop: '1px solid', borderColor: 'divider',
      ...(accent && { boxShadow: `inset 3px 0 0 ${accent}` }),
      '&:hover': { bgcolor: 'grey.50' },
      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
    }}
  >
    {children}
  </Box>
);

const SectionHead: React.FC<{ title: string; moreLabel?: string; onMore?: () => void; dense?: boolean }> = ({
  title, moreLabel, onMore, dense,
}) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: dense ? 0 : 2, py: dense ? 0 : 1.5 }}>
    <Typography variant={dense ? 'subtitle2' : 'h6'} sx={{ fontWeight: 700 }}>{title}</Typography>
    {moreLabel && (onMore ? (
      <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 14 }} />} onClick={onMore}>{moreLabel}</Button>
    ) : <Typography variant="caption" color="text.secondary">{moreLabel}</Typography>)}
  </Stack>
);

export default Dashboard;
